/*
 * Artink collection tabs: an ARIA tablist with automatic activation, a sliding pill
 * behind the active tab, and a card reveal replay when a panel opens.
 * Card reveal and the Choose / gift hand-off come from artink-featured-products.js.
 */

function bootTabs(root) {
  if (!root || root.dataset.tabsBooted === '1') return;
  const list = root.querySelector('[data-tabs-list]');
  const ink = root.querySelector('[data-tabs-ink]');
  const tabs = [...root.querySelectorAll('[data-tab]')];
  if (!list || !tabs.length) return;
  root.dataset.tabsBooted = '1';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const controller = new AbortController();
  const { signal } = controller;

  const panelFor = (tab) => root.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`);

  const moveInk = () => {
    const active = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!ink || !active) return;
    list.style.setProperty('--ink-x', `${active.offsetLeft}px`);
    list.style.setProperty('--ink-w', `${active.offsetWidth}px`);
  };

  const replayCards = (panel) => {
    const cards = [...panel.querySelectorAll('.artink-shop__card')];
    if (reduce) {
      cards.forEach((card) => card.classList.add('is-in'));
      return;
    }
    cards.forEach((card) => card.classList.remove('is-in'));
    requestAnimationFrame(() => {
      requestAnimationFrame(() => cards.forEach((card) => card.classList.add('is-in')));
    });
  };

  const select = (tab, { focus = false } = {}) => {
    if (!tab) return;
    const already = tab.getAttribute('aria-selected') === 'true';
    for (const other of tabs) {
      const on = other === tab;
      other.setAttribute('aria-selected', on ? 'true' : 'false');
      other.tabIndex = on ? 0 : -1;
      const panel = panelFor(other);
      if (panel) panel.hidden = !on;
    }
    if (focus) tab.focus();
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    moveInk();
    const panel = panelFor(tab);
    if (panel && !already) replayCards(panel);
  };

  list.addEventListener(
    'click',
    (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[data-tab]') : null;
      if (tab && tabs.includes(tab)) select(tab);
    },
    { signal }
  );

  list.addEventListener(
    'keydown',
    (event) => {
      const index = tabs.indexOf(document.activeElement);
      if (index < 0) return;
      const rtl = getComputedStyle(list).direction === 'rtl';
      let next = -1;
      switch (event.key) {
        case 'ArrowRight':
          next = rtl ? index - 1 : index + 1;
          break;
        case 'ArrowLeft':
          next = rtl ? index + 1 : index - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      select(tabs[(next + tabs.length) % tabs.length], { focus: true });
    },
    { signal }
  );

  let ro;
  if (window.ResizeObserver) {
    ro = new ResizeObserver(moveInk);
    ro.observe(list);
  } else {
    window.addEventListener('resize', moveInk, { signal });
  }
  // Web fonts change tab widths after first paint.
  document.fonts?.ready.then(() => !signal.aborted && moveInk());

  // Theme editor: selecting a Collection block opens its tab.
  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const tab = tabs.find((t) => t.dataset.blockId === event.detail?.blockId);
      if (tab) select(tab);
    },
    { signal }
  );

  moveInk();
  requestAnimationFrame(() => list.classList.add('is-ready'));

  root._artinkTabsCleanup = () => {
    controller.abort();
    ro?.disconnect();
    root.dataset.tabsBooted = '0';
  };
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-artink-tabs]').forEach(bootTabs);
}

initAll();
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-tabs]').forEach((root) => root._artinkTabsCleanup?.());
});
